import { useEffect, useRef, useState } from 'react';
import { iosBrowser, isAndroid } from '../inapp';

export interface MicChoice {
  /** undefined = check still running (join with the default mic); null = no usable mic (listen only); 'default' = the system's mic. */
  deviceId: string | null | undefined;
  /** The getUserMedia error name when the check failed, reported once the call starts. */
  error?: string;
}

const IOS_APP = { chrome: 'Chrome', firefox: 'Firefox', edge: 'Edge', safari: 'Safari' } as const;

/** Plain-language reason for a getUserMedia failure, with the fix for this device. */
export function micErrorText(err: unknown, ua = navigator.userAgent, touchPoints?: number): string {
  const name = (err as { name?: string })?.name ?? '';
  const ios = iosBrowser(ua, touchPoints);
  if (name === 'NotAllowedError') {
    if (ios === 'safari')
      return 'The microphone is blocked. Tap aA in the address bar → Website Settings → Microphone → Allow, then reload. If it stays blocked: iPhone Settings → Apps → Safari → Microphone.';
    if (ios)
      return `The microphone is blocked. On the iPhone open Settings → Apps → ${IOS_APP[ios]} and turn on Microphone, then reload this page. Or open the link in Safari.`;
    if (isAndroid(ua)) return 'The microphone is blocked. Tap the icon left of the address → Permissions → Microphone → Allow, then reload.';
    return 'The microphone is blocked. Allow it for this site in the browser, and on Windows check Settings → Privacy → Microphone.';
  }
  if (name === 'NotFoundError') return 'No microphone was found on this device.';
  if (name === 'NotReadableError' || name === 'AbortError')
    return ios || isAndroid(ua)
      ? 'The microphone is in use by a phone call or another app. End it, then try again.'
      : 'The microphone is busy or not working. Close Teams, Zoom or other apps using it, then try again.';
  if (name === 'OverconstrainedError') return 'The chosen microphone is no longer available. Pick another one.';
  if (name === 'SecurityError') return 'This browser blocks the microphone here. Open the link in Chrome or Safari.';
  return 'The microphone could not start. You can still join and listen.';
}

const BARS = 12;

export function MicCheck({ onChange }: { onChange: (c: MicChoice) => void }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const stopRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      stopRef.current();
      setError(null);
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error('no mediaDevices'), { name: 'SecurityError' });
        const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true, video: false });
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput');
        setDevices(list);
        const active = stream.getAudioTracks()[0]?.getSettings().deviceId ?? '';
        if (!deviceId && active) setDeviceId(active);
        // iOS has one audio route and its device ids do not reliably survive a new capture: use the system mic.
        onChange({ deviceId: iosBrowser() ? 'default' : deviceId || active || 'default' });
        const ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        let raf = 0;
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (const v of data) sum += ((v - 128) / 128) ** 2;
          setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
          raf = requestAnimationFrame(tick);
        };
        tick();
        stopRef.current = () => {
          cancelAnimationFrame(raf);
          stream.getTracks().forEach((t) => t.stop());
          void ctx.close();
        };
      } catch (err) {
        if (cancelled) return;
        setError(micErrorText(err));
        onChange({ deviceId: null, error: (err as { name?: string })?.name || 'Error' });
      }
    })();
    return () => {
      cancelled = true;
      stopRef.current();
    };
    // onChange is stable for the page's lifetime; re-run only on device or retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, attempt]);

  const lit = Math.round(level * BARS);
  return (
    <div className="list">
      <div>
        <label className="label" htmlFor="mic">
          Microphone
        </label>
        <select id="mic" className="field" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} disabled={!devices.length}>
          {devices.length === 0 && <option value="">No microphone available</option>}
          {devices.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `Microphone ${i + 1}`}
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <div className="error" role="alert">
          {error}{' '}
          <button className="btn sm" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <div className="row">
          <div className="meter" aria-label="Microphone level">
            {Array.from({ length: BARS }, (_, i) => (
              <i key={i} className={i < lit ? 'on' : ''} style={{ height: `${4 + (i < lit ? 14 : 0)}px` }} />
            ))}
          </div>
          <span className="muted small">Speak to test</span>
        </div>
      )}
    </div>
  );
}
